// Admin emails that can bypass Coming Soon gates AND admin-only screens.
// IMPORTANT: This does NOT automatically grant premium access, see
// canAccessPremium() below, which is purely subscription-driven so that
// even admins on Founder Basic see the upgrade modal until they actually
// pay for a tier. This matches the user's 2026-05-14 directive:
// "Every founder is currently considered a Founder Basic until they upgrade."
const ADMIN_EMAILS = [
  "davidcashley87@gmail.com",
  "vscurd@yahoo.com",
  "isaiahdashley2017@gmail.com",
];

export const isAdminUser = (user) => {
  if (!user) return false;
  // While an admin is using "View As", behave as the previewed scenario so
  // admin-only nav / gates / bypasses don't leak into the preview.
  if (user.preview_mode) return false;
  const email = (user.email || "").toLowerCase();
  return ADMIN_EMAILS.includes(email) || user.role === "super_admin" || user.role === "admin" || user.role === "account_helper";
};

// Can this session open the "View As" controller? Real admins, OR an admin
// currently in a preview scenario (so they can always exit).
export const canUseViewAs = (user) => {
  if (!user) return false;
  if (user.preview_mode) return true;
  const email = (user.email || "").toLowerCase();
  return ADMIN_EMAILS.includes(email) || user.role === "super_admin" || user.role === "admin" || user.role === "account_helper";
};

// Paid founder tiers that unlock all premium features.
const PAID_FOUNDER_TIERS = new Set([
  "founder_vip",         // Founders #1-3, owners / lifetime unlimited, never billed
  "founder_individual",
  "founder_family",
  "founder_yearly",
]);

// Paid public-launch subscriptions that unlock all premium features.
const PAID_PUBLIC_PLANS = new Set([
  "explorer_plus_monthly",
  "universe_pass_monthly",
  "universe_pass_yearly",
]);

/**
 * canAccessPremium, returns true ONLY if the user has actively paid for a
 * tier that includes premium features. Founder Basic and Explorer Basic are
 * both free tiers and DO NOT pass this gate, they trigger the upgrade modal.
 *
 * Order of precedence:
 *   1. `user.founder_tier` (set by Stripe webhook on founder upgrades)
 *   2. `user.subscription_plan` (set by Stripe webhook on public-tier upgrades)
 *   3. `user.is_premium` / `entitlements.*`, explicit override flags
 *
 * Note: admins are NOT auto-bypassed any more. If you need to test premium
 * features as an admin, subscribe through the same flow as a real user.
 */
export const canAccessPremium = (user) => {
  if (!user) return false;
  if (PAID_FOUNDER_TIERS.has(user.founder_tier)) return true;
  if (PAID_PUBLIC_PLANS.has(user.subscription_plan)) return true;
  // Per-explorer premium seat (seat/beneficiary subscription model).
  if (user.premium_seat === true) return true;
  if (user.is_premium === true || user.isPremium === true) return true;
  const ent = user.entitlements || {};
  if (ent.is_premium === true || ent.premium === true || ent.can_access_premium === true) return true;
  return false;
};

/**
 * subscriptionDisplayLabel, friendly, single-source label used by the
 * Admin User Management table and anywhere else we surface a user's
 * current plan. Folds the raw stored fields (`founder_tier`,
 * `subscription_plan`, legacy `founder_plan`, `tier_name`) into one of
 * eight canonical labels:
 *   Founder: Basic, Individual, Family, Yearly
 *   Public:  Explorer Basic, Explorer Plus, Universe Pass, Universe Pass Yearly
 *
 * Founders without a paid `founder_tier` default to "Founder Basic" -
 * matches the user's 2026-05-14 directive that every founder starts on Basic.
 */
export const subscriptionDisplayLabel = (user) => {
  if (!user) return "Founder Basic";
  const isFounder = !!(user.founding_member || user.founder_plan || user.founder_tier);
  if (isFounder) {
    const tier = user.founder_tier || user.subscription_plan;
    if (tier === "founder_vip")        return "Founder VIP";
    if (tier === "founder_individual") return "Founder Plus";
    if (tier === "founder_family")     return "Founder Family Pass";
    if (tier === "founder_yearly")     return "Founder Family Pass (Yearly)";
    return "Founder Basic";
  }
  const plan = user.subscription_plan;
  if (plan === "explorer_plus_monthly") return "Explorer Plus";
  if (plan === "universe_pass_monthly") return "Universe Pass";
  if (plan === "universe_pass_yearly")  return "Universe Pass Yearly";
  return "Founder Basic";
};
